/**
 * `vendorForm` — the fire alarm company's own three-page site survey, field
 * for field (spec §5.2, D-041, Task 7).
 *
 * ── Why it exists ────────────────────────────────────────────────────────────
 * `FlsCutSheet.summary` mirrors EFT's one-page RFQ template — the grid the
 * cut sheet has always kept. Their own fillable site-survey form, the one an
 * estimator actually works from, asks for the same facts in a different
 * order across three pages: Project · Fire Alarm · Elevator, a nine-photo
 * checklist, and Conditions. Stephanie's ruling (D-041, decision 15, option
 * A): carry both — the RFQ grid stays exactly as it is, and this is the
 * second view, in the vendor's own order, so the office can fill their PDF
 * (or hand them JSON) without re-reading the whole record to find where each
 * box lives.
 *
 * ── What it is not ───────────────────────────────────────────────────────────
 * Not a second source of truth, and not even a second READING of the
 * document — every member below is read off the rest of `FlsCutSheet`, which
 * has already done the work of pulling each fact off `answers`/`photos`. This
 * file introduces no new fact; it only reshapes ones the sheet already
 * carries into the vendor's page order. `doc` is accepted for the same
 * pure-function contract `buildFlsCutSheet` has, but nothing here reads it —
 * everything this block needs is already on the sheet.
 */

import {
  anyOf,
  presence,
  type CutSheetHoistway,
  type CutSheetNote,
  type CutSheetPhoto,
  type FlsCutSheet,
  type Presence,
} from './flsCutSheet';
import { shotBaseId } from './sections';
import type { SurveyDocument } from './types';

/* ═══════════════════════════════ the shape ═══════════════════════════════ */

export interface VendorFormPhotos {
  facp: CutSheetPhoto[];
  lobbyDetectors: CutSheetPhoto[];
  controllerExterior: CutSheetPhoto[];
  controllerInside: CutSheetPhoto[];
  pit: CutSheetPhoto[];
  hoistway: CutSheetPhoto[];
  cop: CutSheetPhoto[];
  mainDisconnect: CutSheetPhoto[];
  machineRoom: CutSheetPhoto[];
}

export interface VendorForm {
  template: 'eft-site-survey';
  project: {
    name: string | null;
    address: string | null;
    surveyDate: string;
    surveyor: string | null;
  };
  fireAlarm: {
    panelMakeModel: string | null;
    distanceToMachineRoomFt: { roomId: string; roomName: string; feet: number | null }[];
  };
  elevator: {
    stops: { groupId: string; groupName: string; landings: number }[];
    machineRoomLocation: { roomId: string; roomName: string; location: string | null }[];
    /** The same two shots as `photos.controllerExterior`/`controllerInside` — the elevator page's own copy, not a new fact. */
    controllerPhotos: CutSheetPhoto[];
    /** F17, folded across every lobby group — Presence per detector type, not a single grid cell. */
    lobbyDetectionType: { smoke: Presence; heat: Presence; other: Presence };
    /** Notes on F17, F11 or F12 — where a detector is missing, or where one actually is. */
    lobbyDetectorNotes: CutSheetNote[];
  };
  /** The nine required photos, EFT's own numbering, keyed by shot family rather than by entity. */
  photos: VendorFormPhotos;
  /** F19 — how the fire alarm company gets into the building. Not asked since D-045; null on any survey started after it. */
  accessNotes: string | null;
  conditions: {
    /** F10, folded per pit (any device or sprinkler present → yes for that pit), then across pits. */
    pitDevices: Presence;
    pitNotes: CutSheetNote[];
    /** F9 with F13, folded per hoistway the same way, then across hoistways. */
    hoistwayDevices: Presence;
    hoistwayNotes: CutSheetNote[];
    /** F18 — the same building-level cell as `summary.sprinklerSystem`. */
    sprinklerSystem: 'yes' | 'no' | 'unknown' | null;
    /** The same two cells as `summary.shunt.{required,existing}`. */
    shuntRequired: Presence;
    shuntExisting: Presence;
    /** F20, one row per room that answered it. */
    shuntLocation: { roomName: string; text: string }[];
    /** F21, one row per room that answered it. */
    mainDisconnectNotes: { roomName: string; text: string }[];
    /** F2 plus notes attached to the FLS section itself, rather than to one of its questions. */
    fieldNotes: CutSheetNote[];
  };
  /** F16, one row per machine room. */
  recall: { roomId: string; roomName: string; installed: 'yes' | 'no' | 'unknown' | null }[];
}

/* ═══════════════════════════════ building ════════════════════════════════ */

/** Which of the nine boxes a shot's BASE id belongs to — a per-car id (`cab.cop:car-3`) is matched by its base (`cab.cop`), same as everywhere else on the sheet. */
const PHOTO_BOX_BY_SHOT: Record<string, keyof VendorFormPhotos> = {
  'fls.facp': 'facp',
  'fls.facp_sticker': 'facp',
  'fls.facp_nameplate': 'facp',
  'lobby.detector_egress': 'lobbyDetectors',
  'lobby.detector_typical': 'lobbyDetectors',
  'general.controller': 'controllerExterior',
  'general.controller_inside': 'controllerInside',
  'pit.full_pit': 'pit',
  'overhead.top_of_hoistway': 'hoistway',
  'cab.cop': 'cop',
  'electrical.disconnects': 'mainDisconnect',
  'general.wide_room': 'machineRoom',
  'general.door': 'machineRoom',
};

function emptyPhotoBoxes(): VendorFormPhotos {
  return {
    facp: [],
    lobbyDetectors: [],
    controllerExterior: [],
    controllerInside: [],
    pit: [],
    hoistway: [],
    cop: [],
    mainDisconnect: [],
    machineRoom: [],
  };
}

/** F10 per pit: any device or sprinkler ticked is a Yes for that pit; unanswered stays null. */
function pitRow(hoistway: CutSheetHoistway): boolean | null {
  const devices = hoistway.pit.devices;
  return devices ? devices.sprinkler || devices.heat || devices.smoke : null;
}

/** F9 with F13 per hoistway, OR'd the same way `summary.shunt` folds a room's own reach (spec §5.1). */
function hoistwayRow(hoistway: CutSheetHoistway): boolean | null {
  return anyOf([
    hoistway.overhead.sprinklered,
    hoistway.overhead.devices?.smoke ?? null,
    hoistway.overhead.devices?.heat ?? null,
  ]);
}

/**
 * Pure: reads `sheet` (every Task 6 member) and reshapes it into the vendor's
 * own page order. `doc` completes the signature `buildFlsCutSheet` calls this
 * with, but nothing here needs it — every fact on this block already lives on
 * the sheet passed in.
 */
export function buildVendorForm(sheet: Omit<FlsCutSheet, 'vendorForm'>, _doc: SurveyDocument): VendorForm {
  const photos = emptyPhotoBoxes();
  for (const photo of sheet.photos) {
    const box = PHOTO_BOX_BY_SHOT[shotBaseId(photo.shotListId ?? '')];
    if (box) photos[box].push(photo);
  }

  const lobbyDetectionType = {
    smoke: presence(sheet.lobbyGroups.map((group) => group.detectorTypes?.smoke ?? null)),
    heat: presence(sheet.lobbyGroups.map((group) => group.detectorTypes?.heat ?? null)),
    other: presence(sheet.lobbyGroups.map((group) => group.detectorTypes?.other ?? null)),
  };

  const noteIs = (ids: readonly string[]) => (note: CutSheetNote) =>
    note.questionId !== undefined && ids.includes(note.questionId);

  return {
    template: 'eft-site-survey',
    project: {
      name: sheet.job.name,
      address: sheet.job.address,
      surveyDate: sheet.job.date,
      surveyor: sheet.job.surveyor,
    },
    fireAlarm: {
      panelMakeModel: sheet.panel.makeModel,
      distanceToMachineRoomFt: sheet.machineRooms.map((room) => ({
        roomId: room.id,
        roomName: room.name,
        feet: room.facpDistanceFt,
      })),
    },
    elevator: {
      stops: sheet.lobbyGroups.map((group) => ({
        groupId: group.id,
        groupName: group.name,
        landings: group.landings,
      })),
      machineRoomLocation: sheet.machineRooms.map((room) => ({
        roomId: room.id,
        roomName: room.name,
        location: room.location,
      })),
      controllerPhotos: [...photos.controllerExterior, ...photos.controllerInside],
      lobbyDetectionType,
      lobbyDetectorNotes: sheet.notes.filter(noteIs(['F17', 'F11', 'F12'])),
    },
    photos,
    accessNotes: sheet.panel.access,
    conditions: {
      pitDevices: presence(sheet.hoistways.map(pitRow)),
      pitNotes: sheet.notes.filter(noteIs(['F10'])),
      hoistwayDevices: presence(sheet.hoistways.map(hoistwayRow)),
      hoistwayNotes: sheet.notes.filter(noteIs(['F9', 'F13'])),
      sprinklerSystem: sheet.summary.sprinklerSystem,
      shuntRequired: sheet.summary.shunt.required,
      shuntExisting: sheet.summary.shunt.existing,
      shuntLocation: sheet.machineRooms
        .filter(
          (room): room is typeof room & { shuntDisconnectLocation: string } =>
            room.shuntDisconnectLocation !== null,
        )
        .map((room) => ({ roomName: room.name, text: room.shuntDisconnectLocation })),
      mainDisconnectNotes: sheet.machineRooms
        .filter(
          (room): room is typeof room & { mainDisconnectFeed: string } => room.mainDisconnectFeed !== null,
        )
        .map((room) => ({ roomName: room.name, text: room.mainDisconnectFeed })),
      fieldNotes: sheet.notes.filter((note) => note.questionId === 'F2' || note.questionId === undefined),
    },
    recall: sheet.machineRooms.map((room) => ({
      roomId: room.id,
      roomName: room.name,
      installed: room.recallInstalled,
    })),
  };
}
